import React, { useState } from 'react';
import { Stack, Typography, Card, Chip, Button, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HospitalIngestionsModal from './HospitalIngestionsModal';

function ExecutionJobRow({ execution }) {
    const theme = useTheme();
    const [hospitalModalOpen, setHospitalModalOpen] = useState(false);
    const [selectedHospitals, setSelectedHospitals] = useState([]);

    const getStatusChipStyling = (status) => {
        switch (status) {
        case 'ERROR':
        case 'TRANSFORM_FAILED':
        case 'INGESTION_FAILED':
            return {
                backgroundColor: theme.palette.background.negative,
                color: theme.palette.red.main
            };
        case 'INGESTION_STARTED':
            return {
                backgroundColor: theme.palette.background.green,
                color: theme.palette.green.taskText
            };
        case 'TRANSFORMING':
            return {
                backgroundColor: theme.palette.background.yellow,
                color: theme.palette.yellow.inProgress
            };
        case 'TRANSFORMED':
            return {
                backgroundColor: theme.palette.blue.main + '1F',
                color: theme.palette.blue.main
            };
        default:
            return {
                backgroundColor: 'transparent',
                color: 'inherit'
            };
        }
    };

    const getAwsConsoleUrl = (executionArn) => {
        if (!executionArn) return '#';
        
        // Extract region from ARN (arn:aws:states:region:account:execution:...)
        const arnParts = executionArn.split(':');
        const region = arnParts[3] || 'us-east-1';
        
        return `https://${region}.console.aws.amazon.com/states/home?region=${region}#/executions/details/${encodeURIComponent(executionArn)}`;
    };

    const calculateRunTime = (startDate, stopDate) => {
        if (!startDate) return 'N/A';
        
        const start = new Date(startDate);
        const end = stopDate ? new Date(stopDate) : new Date();
        const diffMs = end - start;
        
        const minutes = Math.floor(diffMs / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        
        if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    };

    const handleHospitalClick = (hospitals) => {
        setSelectedHospitals(hospitals);
        setHospitalModalOpen(true);
    };

    const handleCloseModal = () => {
        setHospitalModalOpen(false);
        setSelectedHospitals([]);
    };

    return (
        <Card 
            variant="outlined" 
            sx={{ p: 2 }}
        >
            <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                <Stack sx={{ width: '30%', minWidth: '170px' }} alignItems="flex-start">
                    <Chip 
                        label={execution.details?.status} 
                        size="small" 
                        sx={getStatusChipStyling(execution.details?.status)}
                    />
                </Stack>
                
                <Stack sx={{ width: '35%', minWidth: '200px' }}>
                    {execution.details?.startDate && (
                        <Typography variant="caption">
                            <strong>Started:</strong> {new Date(execution.details.startDate).toLocaleString()}
                        </Typography>
                    )}
                    <Typography variant="caption">
                        <strong>Run Time:</strong> {calculateRunTime(execution.details?.startDate, execution.details?.stopDate)}
                    </Typography>
                </Stack>
                
                <Stack sx={{ width: '20%', minWidth: '60px' }}>
                    {execution.details?.hospitalCodes && execution.details?.status === 'INGESTION_STARTED' && (
                        <Button 
                            variant="text" 
                            size="small" 
                            onClick={() => handleHospitalClick(execution.details.hospitalCodes)}
                            sx={{ 
                                justifyContent: 'flex-start', 
                                textTransform: 'none',
                                fontSize: '12px',
                                p: 0.5
                            }}
                        >
                            View {execution.details.hospitalCodes.length} hospital{execution.details.hospitalCodes.length !== 1 ? 's' : ''}
                        </Button>
                    )}
                </Stack>
                
                <Stack sx={{ width: '15%', minWidth: '100px' }} alignItems="flex-end">
                    <Link 
                        href={getAwsConsoleUrl(execution.executionArn)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '10px',
                            textDecoration: 'none',
                            color: 'text.secondary',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        AWS Console
                    </Link>
                </Stack>
            </Stack>
            
            <HospitalIngestionsModal 
                open={hospitalModalOpen}
                hospitals={selectedHospitals}
                onClose={handleCloseModal}
            />
        </Card>
    );
}

export default ExecutionJobRow;